import { Color, PieceType, GameStatus, GameVariant } from '@draughts/shared';
import type { Piece, BoardState } from '@draughts/shared';
import { serializePosition } from './position-hash.js';

/**
 * Convert a BoardState to standard draughts FEN notation.
 * Format: "<turn>:W<pieces>:B<pieces>"
 * Example: "W:W31,32,K10:B1,2,K22"
 */
export function boardStateToFen(board: BoardState): string {
  const turn = board.currentTurn === Color.WHITE ? 'W' : 'B';

  const whitePieces: string[] = [];
  const blackPieces: string[] = [];

  for (const piece of board.pieces) {
    const prefix = piece.type === PieceType.KING ? 'K' : '';
    const token = `${prefix}${piece.square}`;
    if (piece.color === Color.WHITE) {
      whitePieces.push(token);
    } else {
      blackPieces.push(token);
    }
  }

  // Sort numerically for consistent output
  const sortFn = (a: string, b: string) => {
    const numA = parseInt(a.replace('K', ''));
    const numB = parseInt(b.replace('K', ''));
    return numA - numB;
  };
  whitePieces.sort(sortFn);
  blackPieces.sort(sortFn);

  return `${turn}:W${whitePieces.join(',')}:B${blackPieces.join(',')}`;
}

/**
 * Parse a standard draughts FEN string into a BoardState.
 * Format: "<turn>:W<pieces>:B<pieces>"
 * Example: "B:W9,13,K19,27:B2,4,K20,22"
 */
export function fenToBoardState(fen: string, variant: GameVariant): BoardState {
  const parts = fen.split(':');
  if (parts.length !== 3) {
    throw new Error(`Invalid FEN: expected 3 colon-separated parts, got ${parts.length}`);
  }

  const turnChar = parts[0]!.trim().toUpperCase();
  const currentTurn = turnChar === 'B' ? Color.BLACK : Color.WHITE;

  const pieces: Piece[] = [];

  for (let i = 1; i <= 2; i++) {
    const section = parts[i]!.trim();
    if (section.length === 0) continue;

    const colorChar = section[0]!.toUpperCase();
    const color = colorChar === 'W' ? Color.WHITE : Color.BLACK;
    const pieceList = section.substring(1);

    if (pieceList.length === 0) continue;

    for (const token of pieceList.split(',')) {
      const trimmed = token.trim();
      if (trimmed.length === 0) continue;

      const isKing = trimmed.startsWith('K') || trimmed.startsWith('k');
      const squareStr = isKing ? trimmed.substring(1) : trimmed;
      const square = parseInt(squareStr, 10);

      if (isNaN(square) || square < 1) {
        throw new Error(`Invalid FEN: invalid square "${trimmed}"`);
      }

      pieces.push({
        color,
        type: isKing ? PieceType.KING : PieceType.MAN,
        square,
      });
    }
  }

  const board: BoardState = {
    pieces,
    currentTurn,
    status: GameStatus.PLAYING,
    variant,
    moveHistory: [],
    halfMoveClock: 0,
    positionHistory: [],
  };
  // La posizione di partenza entra nello storico: vedi `createInitialBoard`.
  board.positionHistory = [serializePosition(board)];
  return board;
}
